import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TraditionalPattern from '../components/TraditionalPattern';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showOffer, setShowOffer] = useState(true);
  
  // Jouer l'audio après 2 secondes
  useEffect(() => {
    const timer = setTimeout(() => {
      const audio = new Audio('/images/register_clean.mp3');
      audio.play().catch(err => console.log('Erreur lecture audio:', err));
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    whatsapp: '',
    specialite: '',
    autreSpecialite: '',
    region: '',
    adresse: '',
    email: '',
    motDePasse: '',
    confirmerMotDePasse: '',
    accepteConditions: false,
    photoProfil: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const regions = [
    'Dakar', 'Thiès', 'Saint-Louis', 'Kaolack', 'Ziguinchor',
    'Tambacounda', 'Kolda', 'Fatick', 'Kaffrine', 'Kédougou',
    'Louga', 'Matam', 'Sédhiou', 'Diourbel'
  ];

  const specialites = [
    'Maroquinerie',
    'Poterie',
    'Textile/Tissage',
    'Menuiserie',
    'Bijouterie',
    'Sculpture',
    'Céramique',
    'Vannerie',
    'Forge/Métallurgie',
    'Broderie',
    'Batik/Teinture',
    'Couture',
    'Autres'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photoProfil: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.nom || !formData.telephone || !formData.whatsapp || !formData.specialite || !formData.region || !formData.adresse) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Vérifier si "Autres" est sélectionné et que le champ de spécification est rempli
    if (formData.specialite === 'Autres' && !formData.autreSpecialite.trim()) {
      setError('Veuillez préciser votre spécialité');
      return;
    }

    const phoneRegex = /^(77|78|70|76|75)[0-9]{7}$/;
    if (!phoneRegex.test(formData.telephone.replace(/\s/g, ''))) {
      setError('Le numéro de téléphone doit commencer par 77, 78, 70, 76 ou 75 et contenir 9 chiffres');
      return;
    }

    if (!phoneRegex.test(formData.whatsapp.replace(/\s/g, ''))) {
      setError('Le numéro WhatsApp doit commencer par 77, 78, 70, 76 ou 75 et contenir 9 chiffres');
      return;
    }

    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.motDePasse || !formData.confirmerMotDePasse) {
      setError('Veuillez remplir tous les champs de mot de passe');
      setLoading(false);
      return;
    }

    if (formData.motDePasse.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    if (formData.motDePasse !== formData.confirmerMotDePasse) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (!formData.accepteConditions) {
      setError('Vous devez accepter les conditions d\'utilisation');
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append('nom', formData.nom);
    data.append('telephone', formData.telephone.replace(/\s/g, ''));
    data.append('whatsapp', formData.whatsapp.replace(/\s/g, ''));
    // Utiliser la spécialité personnalisée si "Autres" est sélectionné
    data.append('specialite', formData.specialite === 'Autres' ? formData.autreSpecialite : formData.specialite);
    data.append('region', formData.region);
    data.append('adresse', formData.adresse);
    data.append('motDePasse', formData.motDePasse);
    data.append('accepteConditions', formData.accepteConditions);
    
    if (formData.email && formData.email.trim() !== '') {
      data.append('email', formData.email);
    }
    
    if (formData.photoProfil) {
      data.append('photoProfil', formData.photoProfil);
    }

    const result = await register(data);
    
    if (result.success) {
      navigate('/profile');
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleCallRegistration = () => {
    const message = "Bonjour, je souhaite m'inscrire sur la plateforme Village Artisanal en tant qu'artisan. Pouvez-vous m'aider avec l'inscription ?";
    const whatsappUrl = `https://wa.me/221779317865?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center py-12 px-4">
      {/* Fond artistique avec dégradé */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-accent-700 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"></div>
      
      {/* Motifs traditionnels en arrière-plan */}
      <div className="absolute inset-0 opacity-10 dark:opacity-5">
        <TraditionalPattern variant="geometric" opacity={0.5} className="text-white" />
      </div>
      
      {/* Éléments décoratifs flottants */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-accent-400/30 dark:bg-accent-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-600/40 dark:bg-primary-800/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-yellow-400/20 dark:bg-yellow-600/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '500ms'}}></div>

      {/* Bulle d'offre de lancement - fermable et flottante */}
      {showOffer && (
        <div className="fixed top-4 right-4 z-50 max-w-sm" style={{animation: 'slideInRight 0.5s ease-out'}}>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-2xl p-6 border-2 border-white/30 relative transform hover:scale-105 transition-transform">
            <button
              onClick={() => setShowOffer(false)}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all hover:rotate-90"
              aria-label="Fermer"
            >
              <i className="fas fa-times text-white text-sm"></i>
            </button>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 animate-bounce">
                <i className="fas fa-gift text-white text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">🎉 Offre de Lancement</h3>
                <p className="text-sm text-white/90">
                  Inscription <strong>gratuite</strong> jusqu'au <strong>31 janvier 2026</strong> !
                </p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-white/90">
              <li className="flex items-center gap-2">
                <i className="fas fa-check-circle text-white"></i>
                <span>Profil artisan complet</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fas fa-check-circle text-white"></i>
                <span>Produits illimités</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fas fa-check-circle text-white"></i>
                <span>Visibilité maximale</span>
              </li>
            </ul>
            <div className="mt-4 bg-white/10 rounded-lg p-3 text-center space-y-1">
              <p className="text-sm font-bold">💰 0 CFA jusqu'au 31 janvier 2026</p>
              <p className="text-xs">Puis 2500 CFA/mois ou 25 000 CFA/an</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Carte d'inscription */}
      <div className="relative z-10 max-w-4xl w-full">
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-gray-700/50">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-700 dark:to-primary-900 mb-4 shadow-lg">
              <i className="fas fa-user-plus text-white text-4xl"></i>
            </div>
            <h2 className="text-3xl font-display font-bold text-primary-900 dark:text-primary-300">Inscription Artisan</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Étape {step} sur 2 - {step === 1 ? 'Informations personnelles' : 'Sécurité et validation'}
            </p>
          </div>

          {/* Indicateur de progression */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${step >= 1 ? 'bg-primary-600 dark:bg-primary-700 text-white scale-110' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                {step > 1 ? <i className="fas fa-check"></i> : '1'}
              </div>
              <div className={`h-1 w-24 transition-all ${step >= 2 ? 'bg-primary-600 dark:bg-primary-700' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${step >= 2 ? 'bg-primary-600 dark:bg-primary-700 text-white scale-110' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                2
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-6 flex items-center gap-3">
              <i className="fas fa-exclamation-circle text-red-500 dark:text-red-400"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={step === 1 ? handleNextStep : handleSubmit} className="space-y-6">
            {/* ÉTAPE 1 - Informations personnelles */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Photo de profil */}
                <div className="flex flex-col items-center mb-8">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 text-center">
                    Photo de profil
                  </label>
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary-200 dark:border-primary-700 shadow-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:border-primary-400 dark:group-hover:border-primary-600 transition-all">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Prévisualisation" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center text-gray-400 dark:text-gray-500">
                          <i className="fas fa-camera text-4xl mb-2"></i>
                          <p className="text-xs">Ajouter photo</p>
                        </div>
                      )}
                    </div>
                    <label htmlFor="photoProfil" className="absolute bottom-0 right-0 w-10 h-10 bg-primary-600 dark:bg-primary-700 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-all transform hover:scale-110">
                      <i className="fas fa-camera text-white"></i>
                    </label>
                    <input
                      type="file"
                      id="photoProfil"
                      name="photoProfil"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Format: JPG, PNG (max 5 Mo) - Optionnel</p>
                </div>

                {/* Champs du formulaire en grille */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-user text-primary-600 dark:text-primary-400 mr-2"></i>
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Prénom et nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-phone text-primary-600 dark:text-primary-400 mr-2"></i>
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="77 123 45 67"
                    />
                  </div>

                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fab fa-whatsapp text-green-600 dark:text-green-400 mr-2"></i>
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="77 123 45 67"
                    />
                  </div>

                  <div>
                    <label htmlFor="specialite" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-hammer text-primary-600 dark:text-primary-400 mr-2"></i>
                      Spécialité *
                    </label>
                    <select
                      id="specialite"
                      name="specialite"
                      value={formData.specialite}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Choisir une spécialité</option>
                      {specialites.map((specialite) => (
                        <option key={specialite} value={specialite}>{specialite}</option>
                      ))}
                    </select>
                  </div>

                  {/* Champ pour spécifier une autre spécialité */}
                  {formData.specialite === 'Autres' && (
                    <div className="md:col-span-2">
                      <label htmlFor="autreSpecialite" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <i className="fas fa-edit text-primary-600 dark:text-primary-400 mr-2"></i>
                        Précisez votre spécialité *
                      </label>
                      <input
                        type="text"
                        id="autreSpecialite"
                        name="autreSpecialite"
                        value={formData.autreSpecialite}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Précisez votre spécialité artisanale"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-map-marker-alt text-primary-600 dark:text-primary-400 mr-2"></i>
                      Région *
                    </label>
                    <select
                      id="region"
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Choisir une région</option>
                      {regions.map((region) => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-home text-primary-600 dark:text-primary-400 mr-2"></i>
                      Adresse *
                    </label>
                    <input
                      type="text"
                      id="adresse"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Quartier, ville"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <i className="fas fa-envelope text-primary-600 dark:text-primary-400 mr-2"></i>
                      Email <span className="text-gray-400 dark:text-gray-500 text-xs">(optionnel)</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="exemple@email.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-700 dark:to-primary-900 text-white py-4 px-4 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-900 dark:hover:from-primary-600 dark:hover:to-primary-800 transition-all transform hover:scale-[1.02] shadow-lg"
                >
                  Continuer vers l'étape 2
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>

                {/* Bouton inscription par appel */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">ou</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCallRegistration}
                  className="w-full bg-blue-600 dark:bg-blue-700 border-2 border-blue-600 dark:border-blue-700 text-white py-4 px-4 rounded-xl font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-all transform hover:scale-[1.02] shadow-md"
                >
                  <i className="fab fa-whatsapp mr-2"></i>
                  S'inscrire par WhatsApp
                </button>
              </div>
            )}

            {/* ÉTAPE 2 - Sécurité et validation */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="motDePasse" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-lock text-primary-600 dark:text-primary-400 mr-2"></i>
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    id="motDePasse"
                    name="motDePasse"
                    value={formData.motDePasse}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Minimum 6 caractères"
                  />
                </div>

                <div>
                  <label htmlFor="confirmerMotDePasse" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-lock text-primary-600 dark:text-primary-400 mr-2"></i>
                    Confirmer le mot de passe *
                  </label>
                  <input
                    type="password"
                    id="confirmerMotDePasse"
                    name="confirmerMotDePasse"
                    value={formData.confirmerMotDePasse}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Retapez le mot de passe"
                  />
                </div>

                {/* Case à cocher obligatoire */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-600">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="accepteConditions"
                      checked={formData.accepteConditions}
                      onChange={handleChange}
                      required
                      className="mt-1 w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      J'accepte les{' '}
                      <Link to="/conditions-utilisation" className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-800 dark:hover:text-primary-300 underline" target="_blank">
                        conditions d'utilisation
                      </Link>
                      {' '}et la{' '}
                      <Link to="/politique-confidentialite" className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-800 dark:hover:text-primary-300 underline" target="_blank">
                        politique de confidentialité
                      </Link>
                      {' '}du Village Artisanal *
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 py-4 px-4 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Retour
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-700 dark:from-green-700 dark:to-green-800 text-white py-4 px-4 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 dark:hover:from-green-600 dark:hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] shadow-lg"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Inscription en cours...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-2"></i>
                        Finaliser l'inscription
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Déjà inscrit ?{' '}
              <Link to="/login" className="text-primary-800 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 font-semibold transition-colors">
                Connectez-vous ici
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;